// Home.js — the landing page: every page in the app, grouped as the sidebar groups them.
//
// The list is derived from PAGE_CONFIG rather than kept here, because a second hand-maintained
// copy drifts. Adding a page to PAGE_CONFIG puts it here too.
//
// What's shown is filtered by the same `can()` the sidebar and the route guard use, so nobody is
// offered a page that would refuse them on arrival.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, List } from 'lucide-react';
import { TbBar, TbDivider, TbLogo, TbIconBtn } from './Toolbar';
import { pageIcons, PAGE_CONFIG, SECTIONS } from './Sidebar';
import { useAuth } from './AuthContext';
import './Home.css';

// A line on what each page is for. Keyed by page id, and entirely optional — a page with no
// entry still appears, just without the subtitle, so a new page can never be silently dropped.
const DESCRIPTIONS = {
  '/tactical-xml': 'StatsBomb, SkillCorner and Melbourne City XML generator',
  '/permissions': 'User accounts and per-page access',
};

function Home() {
  const [collapsed, setCollapsed] = useState(false);
  const { can, user } = useAuth();

  const sections = useMemo(() => {
    const allowed = PAGE_CONFIG.filter((p) => p.section !== 'top' && can(p.id));
    return SECTIONS
      .map((title) => ({ title, pages: allowed.filter((p) => p.section === title) }))
      .filter((s) => s.pages.length > 0);
  }, [can]);

  const total = sections.reduce((n, s) => n + s.pages.length, 0);

  return (
    <div className="home-container">
      <TbBar className="home-toolbar">
        <TbLogo />
        <TbDivider />
        <span className="home-greeting">
          {user && user.displayName ? user.displayName : 'Welcome'}
        </span>
        <span className="home-count">{total} {total === 1 ? 'page' : 'pages'}</span>
        <div style={{ flex: 1 }} />
        <TbIconBtn
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? 'Grid view' : 'Compact view'}
        >
          {collapsed ? <LayoutGrid size={14} /> : <List size={14} />}
        </TbIconBtn>
      </TbBar>

      <div className={`home-sections ${collapsed ? 'collapsed' : ''}`}>
        {sections.length === 0 && (
          <div className="home-empty-note">
            You don’t have access to any pages yet — ask an administrator to grant some.
          </div>
        )}
        {sections.map((section) => (
          <div key={section.title} className="home-section">
            <div className="home-section-header">
              <h2 className="home-section-title">{section.title}</h2>
              <div className="home-section-line" />
            </div>
            <div className={`home-grid ${collapsed ? 'collapsed' : ''}`}>
              {section.pages.map((page) => (
                <Link key={page.id} to={page.id} className="home-card">
                  <div className={`home-card-content ${collapsed ? 'collapsed' : ''}`}>
                    <span className="home-card-icon">{pageIcons[page.icon]}</span>
                    <div className="home-text-wrapper">
                      <h2 className="home-page-title">{page.name}</h2>
                      {!collapsed && DESCRIPTIONS[page.id] && (
                        <p className="home-description">{DESCRIPTIONS[page.id]}</p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Home;
